"use client";

import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useState, useRef, useCallback, MouseEvent } from "react";
import styles from "./whatsapp.module.css";

interface TestimonialsProps {
  className?: string;
}

interface Message {
  role: "client" | "niumora";
  content: string;
  timestamp?: string;
}

interface Conversation {
  client: {
    name: string;
    initials: string;
  };
  messages: Message[];
}

const conversations: Conversation[] = [
  {
    client: {
      name: "Handy Cats",
      initials: "HC",
    },
    messages: [
      {
        role: "niumora",
        content: "Hey guys, won't you write a testimonial for our website?",
        timestamp: "10:03",
      },
      {
        role: "client",
        content: "Sure, would love to!",
        timestamp: "11:17",
      },
      {
        role: "client",
        content: "We put our whole team on the AI Bootcamp and the results have been amazing. Our employees have been automating workflows, using GenAI for ideation and even requesting new products. I couldn't recommend it enough! 🚀",
        timestamp: "11:35",
      },
    ],
  },
  {
    client: {
      name: "The Frame Room",
      initials: "FR",
    },
    messages: [
      {
        role: "niumora",
        content: "Hey Chris, won't you share a testimonial about the work we did for you?",
        timestamp: "14:15",
      },
      {
        role: "client",
        content: "You write whatever :)",
        timestamp: "14:19",
      },
      {
        role: "niumora",
        content: "No dude I need you to write it pls",
        timestamp: "14:22",
      },
      {
        role: "client",
        content: "Niumora helped us build an internal quoting tool with AI integrated which has increased our quoting turnaround by 10x giving us time to focus on what matters. Highly recommended! 👍",
        timestamp: "14:26",
      },
    ],
  },
  {
    client: {
      name: "Tokyo Brand House",
      initials: "TBH",
    },
    messages: [
      {
        role: "niumora",
        content: "Hey Stu, won't you write a testimonial for my new website?",
        timestamp: "17:41",
      },
      {
        role: "client",
        content: "Niumora has helped us on a number of occasions and we have been very happy to help them too. Thanks Alexis! 🙏",
        timestamp: "17:45",
      },
      {
        role: "client",
        content: "Why don't you just post the messages as testimonials?",
        timestamp: "17:47",
      },
      {
        role: "niumora",
        content: "That's an awesome idea! 🤩",
        timestamp: "17:48",
      },
    ],
  },
  {
    client: {
      name: "New Client",
      initials: "NC",
    },
    messages: [
      {
        role: "niumora",
        content: "Hey dude, won't you share a bit about what it's been like working with us compared to traditional dev cycles? I'd like to post it on our website.",
        timestamp: "19:02",
      },
      {
        role: "client",
        content: "We've been working on a project with Niumora now for a couple weeks and the speed and collaboration is night and day from traditional development cycles.",
        timestamp: "19:09",
      },
    ],
  },
  {
    client: {
      name: "Handy Cats",
      initials: "HC",
    },
    messages: [
      {
        role: "niumora",
        content: "Hey guys, won't you please share about how we helped with the SEO automation?",
        timestamp: "15:03",
      },
      {
        role: "client",
        content: "okay no problem",
        timestamp: "17:25",
      },
      {
        role: "client",
        content: "Niumora helped us with an SEO automation to improve 427 pages meta titles and descriptions in under an hour, this would normally take our team days.",
        timestamp: "17:29",
      },
    ],
  },
];

export default function Testimonials({ className }: TestimonialsProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);

  const handleMouseDown = (e: MouseEvent) => {
    if (!scrollContainerRef.current) return;
    
    setIsDragging(true);
    setIsAutoScrolling(false);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;

    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  }, [isDragging, startX, scrollLeft]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
    setIsAutoScrolling(true);
  }, []);

  return (
    <section className={cn("bg-slate-900 py-24", className)}>
      <div className="container">
      <div className="text-center mb-12">
          <h2 className="text-white text-3xl md:text-4xl font-bold font-display mb-4">
            Client <span className="gradient-text">Testimonials</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Here's what our clients have to say about their experience working with Niumora.
          </p>
        </div>

        <div className="relative">
          {/* Gradient overlays */}
          <div className="pointer-events-none absolute left-0 top-0 z-10 h-full w-32 bg-gradient-to-r from-slate-900 to-transparent" />
          <div className="pointer-events-none absolute right-0 top-0 z-10 h-full w-32 bg-gradient-to-l from-slate-900 to-transparent" />

          <div
            ref={scrollContainerRef}
            className={cn(styles.slider, isDragging && styles.dragging)}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          >
            <div className={`${styles.sliderContent} ${isAutoScrolling && !isDragging && styles.autoScroll}`}>
              {/* Original testimonials */}
              {conversations.map((conversation, index) => (
                <div
                  key={`${conversation.client.name}-${index}`}
                  className="w-[320px] flex-shrink-0"
                >
                  <div className="relative min-h-fit overflow-hidden rounded-lg bg-[#111b21]">
                    <div className={styles.whatsappBg} />
                    <div className="relative">
                      {/* WhatsApp Header */}
                      <div className="bg-[#202c33] px-[16px] py-[10px] flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-[#00a884] text-white">
                            {conversation.client.initials}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <h3 className="font-medium text-[16px] leading-[21px] text-white">
                            {conversation.client.name}
                          </h3>
                        </div>
                      </div>
                      
                      {/* Chat Messages */}
                      <div className="relative">
                        <div className="px-[20px] py-[10px]">
                          <div className="flex flex-col gap-[15px]">
                            {conversation.messages.map((message, i) => (
                              <div
                                key={i}
                                className={`${styles.message} ${
                                  message.role === "client"
                                    ? styles.clientMessage
                                    : styles.niumoraMessage
                                }`}
                              >
                                <p className={`${styles.messageContent} text-[14.2px] leading-[19px] text-white`}>
                                  {message.content}
                                </p>
                                <span className={styles.timestamp}>
                                  {message.timestamp}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Duplicate testimonials for seamless loop */}
              {conversations.map((conversation, index) => (
                <div
                  key={`${conversation.client.name}-duplicate-${index}`}
                  className="w-[320px] flex-shrink-0"
                >
                  <div className="relative min-h-fit overflow-hidden rounded-lg bg-[#111b21]">
                    <div className={styles.whatsappBg} />
                    <div className="relative">
                      {/* WhatsApp Header */}
                      <div className="bg-[#202c33] px-[16px] py-[10px] flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-[#00a884] text-white">
                            {conversation.client.initials}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <h3 className="font-medium text-[16px] leading-[21px] text-white">
                            {conversation.client.name}
                          </h3>
                        </div>
                      </div>
                      
                      {/* Chat Messages */}
                      <div className="relative">
                        <div className="px-[20px] py-[10px]">
                          <div className="flex flex-col gap-[15px]">
                            {conversation.messages.map((message, i) => (
                              <div
                                key={i}
                                className={`${styles.message} ${
                                  message.role === "client"
                                    ? styles.clientMessage
                                    : styles.niumoraMessage
                                }`}
                              >
                                <p className={`${styles.messageContent} text-[14.2px] leading-[19px] text-white`}>
                                  {message.content}
                                </p>
                                <span className={styles.timestamp}>
                                  {message.timestamp}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}